/* eslint-disable react/require-default-props */
import { useCallback, type ReactNode, useContext } from 'react'
import type { Entry } from 'contentful'

import ImageContentful from '@/components/ui/image-contentful'
import Video from '@/components/ui/video'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'
import Link from '@/components/link-component'
import RichText from '@/components/ui/rich-text'
import HubiloForm from '@/components/ui/hubilo-form/hubilo-form'
import HubSpotForm from '@/components/ui/hubspot-form'
import Image from '@/components/ui/image'
import Lottie from '@/components/lottie'
import BarGraph from '@/components/bar-graph'
import Code from '@/components/ui/code/code'
import CodeTabs from '@/components/ui/code/code-tabs'
import { cx } from '@/utils/strings'
import { ThemeContext } from '@/components/theme-provider'
import { getBackground, checkOverideDarkMode } from '@/utils/backgrounds'
import colors from '@/constants/colors'
import { renderTags } from '../article-list-card'
import gradients from '@/constants/gradients'

import {
  isTypeComponentHubiloForm,
  isTypeComponentHubSpotForm,
  type TypeComponentCard,
  type TypeComponentCtaLink,
  type TypeComponentLottie,
  type TypeResourceImage,
  type TypeSectionBarGraph,
  type TypeSectionSetOfCards,
} from '@/types/ctf'
import CtaLink from './cta-link'

export type DynamicCardImage = {
  image: {
    description: string
    publicUrl: string
    title: string
  }
}

type CtaLinkEntry = TypeComponentCtaLink<'WITHOUT_UNRESOLVABLE_LINKS'>
type CardEntry = TypeComponentCard<'WITHOUT_UNRESOLVABLE_LINKS'>
type SetOfCardsEntry = TypeSectionSetOfCards<'WITHOUT_UNRESOLVABLE_LINKS'>
type ResourceImageEntry = TypeResourceImage<'WITHOUT_UNRESOLVABLE_LINKS'>
type LottieEntry = TypeComponentLottie<'WITHOUT_UNRESOLVABLE_LINKS'>
type BarGraphEntry = TypeSectionBarGraph<'WITHOUT_UNRESOLVABLE_LINKS'>

export type CardProps = Omit<CardEntry['fields'], 'actions' | 'subtext' | 'internalName'> &
  Omit<SetOfCardsEntry['fields'], 'actions' | 'internalName'> & {
    className?: string
    subtext?: CardEntry['fields']['subtext'] | string
    actions?: DynamicActionProps[]
    columnCount?: number
    isHero: boolean
    transparentBg?: boolean
    tags?: string[]
  }

export default function Card({
  className,
  actions,
  asset,
  autoAdjustSvgColor,
  cardIconLocation,
  cardIconSize = '1/3',
  columnCount = 1,
  headline,
  image,
  isHero = false,
  stackImageOverText,
  subtext,
  subtextAlignment,
  tagline,
  tags,
  imageType,
  showHeroImageOnMobile = false,
  transparentBg = false,
  heroLayout,
  heroBackgroundColor,
  imageDarkMode,
  imageBackgroundColor,
  unconstrained = false,
}: CardProps) {
  const soleAction: any =
    actions?.length === 1 && actions.at(0)?.type !== 'componentHubspotCta'
      ? (actions[0] as CtaLinkEntry & { to: string })
      : null

  const soleActionLink =
    (soleAction as any)?.link?.sys?.contentType?.sys?.id === 'componentLink'
      ? (soleAction as any)?.link?.fields?.url
      : (soleAction as { to: string })?.to

  const hasLinkArrow = soleAction && !soleAction?.label && !!soleActionLink

  const isSvg =
    (image?.fields?.file?.contentType || asset?.fields?.image?.fields?.file?.contentType) === 'image/svg+xml'

  if (imageType === undefined) {
    imageType = isSvg ? 'Spot' : 'Image'
  }

  if ((isSvg && tagline === 'Resource') || (isSvg && tagline === 'Blog') || (isSvg && isHero)) {
    imageType = 'Image'
  }

  const LinkWrap = useCallback(
    ({
      children,
      to,
      plausibleEventName = '',
    }: {
      children: ReactNode
      to?: string | null | false
      plausibleEventName: string
    }) => (
      <>
        {to ? (
          <Link
            className={`flex w-full text-secondary-800 dark:text-secondary-50 ${plausibleEventName?.trim()}`}
            to={to}
            aria-label={`go to ${to}`}
          >
            {children}
          </Link>
        ) : (
          children
        )}
      </>
    ),
    []
  )
  const { currentTheme } = useContext(ThemeContext)

  const Tagline = useCallback(() => {
    const Wrapper = isHero ? 'h1' : 'div'
    return (
      <Wrapper data-testid="card-tagline" className={cx('text-eyebrow uppercase', isHero ? 'mb-0 pb-0' : 'pb-5')}>
        {tagline}
      </Wrapper>
    )
  }, [tagline, isHero])

  const LinkRenderer = useCallback((node: { data: { uri: string } }, children: ReactNode) => {
    return (
      <Link className="text-primaryBlue" to={node.data.uri}>
        {children}
      </Link>
    )
  }, [])

  const renderImageContentful = () => (
    <div className="relative">
      <ImageContentful
        {...(image?.fields as any)}
        darkModeImage={imageDarkMode?.fields}
        className={cx(
          (!imageType || imageType === 'Image') && 'w-full',
          'overflow-hidden',
          imageType === 'Spot' && `w-[160px]! @3xl:w-full! rounded-lg`,
          imageType === 'Icon' && `w-[60px]!  @3xl:w-full! rounded-lg`,
          imageType === 'Image' &&
            (headline || subtext) &&
            heroLayout === 'Horizontal' &&
            `rounded-tl-lg rounded-tr-lg @3xl:rounded-lg`,
          imageType === 'Image' && !headline && !subtext && `rounded-lg`,
          !headline && !subtext && !asset && !actions && `max-w-[1000px] mx-auto`,
          isSvg && tagline === 'Blog' && 'p-6',
          'dark:bg-transparent!',
          heroLayout === 'Vertical' && `max-w-[1000px] mx-auto rounded-lg`,
          isSvg && !autoAdjustSvgColor && `[&_svg]:text-transparent`,
          imageBackgroundColor === 'White' && `dark:bg-white! `,
          imageType === 'Image' && imageBackgroundColor === 'White' && `[&_img]:p-4 [&_svg]:p-4`
        )}
        loading={isHero ? 'eager' : 'lazy'}
        invertSvgForDarkMode={currentTheme === 'dark' && isSvg}
      />
    </div>
  )

  const isHubSpotForm = asset && isTypeComponentHubSpotForm(asset)
  const isHubiloForm = asset && isTypeComponentHubiloForm(asset)

  return (
    <div
      className={cx(
        '@container flex',
        className,
        isHero &&
          heroLayout === 'Vertical' &&
          heroBackgroundColor &&
          `after:top-0 after:content[""] after:block after:w-full after:absolute ${getBackground(heroBackgroundColor, 'after')}`,
        heroLayout === 'Vertical' && unconstrained && heroBackgroundColor
          ? 'after:bottom-0 after:left-0'
          : 'relative after:bottom-[20%] after:rounded-b-lg '
      )}
    >
      <LinkWrap to={hasLinkArrow && soleActionLink} plausibleEventName={soleAction?.plausibleEventName}>
        <div
          className={cx(
            'z-10 @3xl:flex space-y-6 @3xl:space-y-0!  h-full w-full rounded-lg',
            transparentBg || isHero || columnCount === 1
              ? 'bg-transparent'
              : 'bg-white border-b-2 border-b-secondary-100 @3xl:border-none dark:bg-secondary-800',
            hasLinkArrow && tagline?.length && `bg-white dark:bg-secondary-800`,
            imageType !== 'Image' && !transparentBg && columnCount > 1 && `p-6`,
            cardIconLocation === 'Right' && heroLayout === 'Horizontal' && '@3xl:flex-row-reverse @3xl:space-x-reverse',
            stackImageOverText === false && 'flex-row gap-8 items-center',
            (isHubSpotForm || isHubiloForm) && cardIconLocation === 'Right' && 'flex-col-reverse',
            hasLinkArrow &&
              `group relative transition-transform duration-300 ease-out md:hover:scale-[1.01] md:before:content-[""]
              before:absolute before:-z-10 before:top-0 before:rounded-lg before:left-0 before:h-full before:w-full before:shadow-2xl before:shadow-black before:opacity-0 md:hover:before:opacity-30
              before:transition-opacity before:duration-[250ms] before:ease-out`,

            heroLayout === 'Horizontal' && `@3xl:flex-row @3xl:space-x-6 items-center`,
            heroLayout === 'Vertical' && `flex flex-col flex-col-reverse`
          )}
          data-testid="card-container"
        >
          {(image || asset) && (
            <div
              data-testid="card-asset"
              className={cx(
                `relative`,
                imageType === 'Image' && !transparentBg && columnCount > 1 && `@3xl:p-6`,
                hasLinkArrow && tagline?.length && `@3xl:p-6`,
                heroLayout === 'Horizontal' &&
                  imageType === 'Image' &&
                  cardIconLocation !== 'Right' &&
                  !transparentBg &&
                  `@3xl:pr-0!`,
                heroLayout === 'Horizontal' &&
                  imageType === 'Image' &&
                  cardIconLocation === 'Right' &&
                  !transparentBg &&
                  `@3xl:pl-0!`,

                asset?.sys?.contentType?.sys?.id === 'componentCodeTabs' && 'w-full flex-[1 0 auto]',
                asset?.sys?.contentType?.sys?.id === 'componentCode' && 'lg:[&_.bw-code-container]:max-h-[38rem]',
                heroLayout === 'Horizontal' && (cardIconSize === '1/3' || cardIconSize === '1/4') && '@3xl:w-1/3',
                heroLayout === 'Horizontal' && (cardIconSize === '1/2' || cardIconSize === 'Full') && '@3xl:w-1/2',
                heroLayout === 'Horizontal' && cardIconSize === '2/3' && '@3xl:w-2/3',
                imageType === 'Image' && !subtext && !headline && `@3xl:w-full!`,
                ((isSvg && tagline === 'Resource') || (isSvg && tagline === 'Blog')) && 'w-full @3xl:w-1/3',
                isHero && !showHeroImageOnMobile && !isHubSpotForm && !isHubiloForm && `hidden @3xl:sm:block`,
                ((isHero && isHubSpotForm) || (isHero && isHubiloForm)) && `@3xl:self-start block pl-0!`,
                isHero && heroLayout === 'Vertical' && 'w-full p-6',
                isHero && heroLayout === 'Horizontal' && `${getBackground(heroBackgroundColor)} rounded-lg @3xl:p-0! `,
                isHero &&
                  heroBackgroundColor &&
                  isHubSpotForm &&
                  'hs-form-w-gradient-background py-10! pl-5! pr-5! @3xl:p-10!',
                isHero && heroBackgroundColor && heroLayout === 'Vertical' && isHubSpotForm && 'max-w-[680px] mx-auto'
              )}
            >
              {image ? (
                (() => {
                  const shouldRenderWithWrapper = (isSvg && tagline === 'Resource') || (isSvg && tagline === 'Blog')
                  imageType = shouldRenderWithWrapper ? 'Image' : imageType

                  return shouldRenderWithWrapper ? (
                    <div className="rounded-t-lg @3xl:rounded-lg bg-primaryBlue [&_*]:aspect-video w-full">
                      {renderImageContentful()}
                    </div>
                  ) : (
                    renderImageContentful()
                  )
                })()
              ) : (
                <>
                  {isHubSpotForm && (
                    <div className={cx('w-full')}>
                      <HubSpotForm {...asset.fields} />
                    </div>
                  )}
                  {isHubiloForm && (
                    <div className={cx('w-full')}>
                      <HubiloForm {...asset.fields} id={asset.sys.id} />
                    </div>
                  )}

                  {asset?.sys?.contentType?.sys?.id === 'componentVideo' && (
                    <div className="rounded-tl-lg rounded-tr-lg @3xl:rounded-lg overflow-hidden">
                      <Video {...(asset as any)} />
                    </div>
                  )}

                  {asset?.sys?.contentType?.sys?.id === 'resourceImage' && (
                    <Image
                      image={(asset as ResourceImageEntry)?.fields?.image}
                      {...asset}
                      className={cx(
                        'w-full rounded-lg',
                        isSvg && `[&_svg]:h-full [&_svg]:w-full`,
                        isSvg && !autoAdjustSvgColor && `[&_svg]:text-transparent`,
                        isSvg && !headline && !subtext && !asset && !actions && `max-w-[1000px]`,
                        isSvg && tagline === 'Resource' && `bg-transparent`,
                        imageType === 'Spot' && `w-[160px]! @3xl:w-full! rounded-lg`,
                        imageType === 'Icon' && `w-[60px]!  @3xl:w-full! rounded-lg`,
                        'dark:bg-transparent'
                      )}
                      loading={isHero ? 'eager' : 'lazy'}
                      invertSvgForDarkMode={currentTheme === 'dark' && isSvg}
                    />
                  )}
                  {asset?.sys?.contentType?.sys?.id === 'componentLottie' && (
                    <Lottie {...(asset as LottieEntry)?.fields} />
                  )}
                  {asset?.sys?.contentType?.sys?.id === 'sectionBarGraph' && (
                    <BarGraph {...(asset as BarGraphEntry)?.fields} />
                  )}
                  {asset?.sys?.contentType?.sys?.id === 'componentCode' && <Code {...asset?.fields} />}
                  {asset?.sys?.contentType?.sys?.id === 'componentCodeTabs' && <CodeTabs {...asset?.fields} />}
                </>
              )}
            </div>
          )}

          {(headline || subtext || actions) && (
            <div
              data-testid="card-content"
              className={cx(
                imageType === 'Image' && !transparentBg && !isHero && columnCount > 1 && `px-6 pb-6 @3xl:p-6`,
                hasLinkArrow && tagline?.length && `px-6 pb-6 @3xl:p-6`,
                cardIconLocation !== 'Right' &&
                  imageType === 'Image' &&
                  !transparentBg &&
                  heroLayout === 'Horizontal' &&
                  `@3xl:pl-0!`,
                cardIconLocation === 'Right' &&
                  imageType === 'Image' &&
                  !transparentBg &&
                  heroLayout === 'Horizontal' &&
                  `@3xl:pr-0!`,
                heroLayout === 'Horizontal' && (cardIconSize === '1/3' || cardIconSize === '1/4') && `@3xl:w-2/3`,
                heroLayout === 'Horizontal' && (cardIconSize === '1/2' || cardIconSize === 'Full') && `@3xl:w-1/2`,
                heroLayout === 'Horizontal' && cardIconSize === '2/3' && `@3xl:w-1/3`,
                ((isSvg && tagline === 'Resource') || (isSvg && tagline === 'Blog')) && '@3xl:w-2/3',
                !headline && !subtext && !asset && !actions && `hidden`,
                asset && !headline && !subtext && !image && !actions && `hidden`,
                !asset && !image && isHero && `text-center xl:w-4/5 m-auto`,
                isHero && heroLayout === 'Vertical' && `text-center w-full px-6 pt-16 lg:pt-17`,
                isHero && heroLayout === 'Horizontal' && `py-3 lg:py-6`,
                !asset && !image && `pt-6`
              )}
            >
              {tagline && <Tagline />}
              {headline && (
                <div data-testid="card-headline">
                  {isHero ? (
                    <h1 className="h1 leading-tight mb-4 hyphens-auto firefox:hyphens-manual card-hero-h1">
                      {headline}
                    </h1>
                  ) : (
                    <h3 className="h4">{headline}</h3>
                  )}
                </div>
              )}
              {tags && renderTags(tags)}
              <div
                data-testid="card-subtext"
                className={cx(
                  '[&_h2]:h3 [&_p:last-of-type]:mb-0',
                  subtextAlignment === 'Center' && 'text-center',
                  subtextAlignment === 'Right' && 'text-right',
                  isHero && heroLayout === 'Vertical' && `m-auto @3xl:max-w-2/3`,
                  hasLinkArrow && tagline?.length && 'mb-16 @3xl:mb-6'
                )}
              >
                {typeof subtext === 'string' && <p>{subtext}</p>} {/* Blog Page/Landing Page cards pass a string */}
                {typeof subtext === 'object' && (
                  <RichText
                    body={subtext}
                    a={LinkRenderer}
                    renderImage={(ref) => <Image image={ref} showCaption={false} loading={isHero ? 'eager' : 'lazy'} />}
                  />
                )}
              </div>
              {hasLinkArrow && tagline?.length && (
                <div
                  className={cx(
                    'border-t border-secondary-100 absolute bottom-0 left-6 right-6 py-5 text-right @3xl:relative @3xl:left-auto @3xl:right-auto'
                  )}
                >
                  <CtaLink asButton style="Arrow Link" className="font-bold">
                    Read more
                  </CtaLink>
                </div>
              )}
              {!hasLinkArrow && actions && (
                <div>
                  <div
                    className={cx(
                      'flex flex-wrap gap-4 items-center pt-4',
                      ((!asset && !image && isHero) || heroLayout === 'Vertical') && `justify-center`
                    )}
                  >
                    {actions.map((action) => {
                      return (
                        <DynamicAction
                          key={action.id}
                          {...action}
                          data-testid="card-cta"
                          size={!isHero ? 'Smaller' : 'Base'}
                          overideDarkMode={checkOverideDarkMode(heroBackgroundColor)}
                          hasDarkBg={
                            heroLayout === 'Vertical' &&
                            heroBackgroundColor !== colors.white &&
                            heroBackgroundColor !== colors.transparent &&
                            !checkOverideDarkMode(heroBackgroundColor)
                          }
                        />
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </LinkWrap>
    </div>
  )
}
